'use client';

import { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import styles from '../styles/DateTimePicker.module.css';

interface DateTimePickerProps {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
}

export default function DateTimePicker({
  selected,
  onChange,
  minDate,
  maxDate,
}: DateTimePickerProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [mode, setMode] = useState<'date' | 'time'>('date');
  const [selectedDate, setSelectedDate] = useState<Date | null>(selected);
  // Helper function to round minutes to nearest multiple of 10
  const roundMinuteToTen = (minute: number): number => {
    return Math.round(minute / 10) * 10;
  };

  // Round minutes up to next multiple of 10 (for min time today: e.g. 14:02 -> 14:10)
  const roundMinuteUpToTen = (minute: number): number => {
    if (minute % 10 === 0) return minute;
    return Math.ceil(minute / 10) * 10;
  };

  // When selected date is today, return the minimum selectable time (now rounded up to next 10 min)
  const getMinTimeForSelectedDate = (): { hour: number; minute: number } | null => {
    if (!selectedDate) return null;
    const now = new Date();
    const isToday =
      selectedDate.getDate() === now.getDate() &&
      selectedDate.getMonth() === now.getMonth() &&
      selectedDate.getFullYear() === now.getFullYear();
    if (!isToday) return null;
    let hour = now.getHours();
    let minute = roundMinuteUpToTen(now.getMinutes());
    if (minute === 60) {
      minute = 0;
      hour += 1;
    }
    if (hour >= 24) hour = 23;
    return { hour, minute };
  };

  const [selectedTime, setSelectedTime] = useState<{ hour: number; minute: number } | null>(
    selected ? { hour: selected.getHours(), minute: roundMinuteToTen(selected.getMinutes()) } : null
  );
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [useFixedPosition, setUseFixedPosition] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const clockRef = useRef<HTMLDivElement>(null);
  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef({ hour: false, minute: false });
  const scrollTimeoutRef = useRef({ hour: null as NodeJS.Timeout | null, minute: null as NodeJS.Timeout | null });
  const userScrollingRef = useRef({ hour: false, minute: false });
  const userClickingRef = useRef({ hour: false, minute: false });
  const isInitializingRef = useRef(false);

  // Initialize selectedDate and selectedTime from selected prop
  useEffect(() => {
    if (selected) {
      setSelectedDate(selected);
      setSelectedTime({ hour: selected.getHours(), minute: roundMinuteToTen(selected.getMinutes()) });
    }
  }, [selected]);

  // Extract hour and minute for stable dependencies
  const currentHour = selectedTime?.hour ?? null;
  const currentMinute = selectedTime?.minute ?? null;

  // Scroll to selected hour value when it changes (only if user is not scrolling or clicking)
  useEffect(() => {
    if (currentHour === null || !hourRef.current || isScrollingRef.current.hour || userScrollingRef.current.hour || userClickingRef.current.hour || isInitializingRef.current) return;
    
    // Find the element closest to the center of the viewport, not just the first one
    const container = hourRef.current;
    const containerRect = container.getBoundingClientRect();
    const centerY = containerRect.top + containerRect.height / 2;
    
    const allMatchingElements = Array.from(container.querySelectorAll(`[data-hour="${currentHour}"]`)) as HTMLElement[];
    if (allMatchingElements.length === 0) return;
    
    // Find the element closest to the center
    let closestElement: HTMLElement | null = null;
    let closestDistance = Infinity;
    
    allMatchingElements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      const elementCenterY = rect.top + rect.height / 2;
      const distance = Math.abs(centerY - elementCenterY);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestElement = element;
      }
    });
    
    if (closestElement) {
      // Use a small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        if (!userScrollingRef.current.hour && !userClickingRef.current.hour) {
          closestElement!.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [currentHour]);

  // Scroll to selected minute value when it changes (only if user is not scrolling or clicking)
  useEffect(() => {
    if (currentMinute === null || !minuteRef.current || isScrollingRef.current.minute || userScrollingRef.current.minute || userClickingRef.current.minute || isInitializingRef.current) return;
    
    // Find the element closest to the center of the viewport, not just the first one
    const container = minuteRef.current;
    const containerRect = container.getBoundingClientRect();
    const centerY = containerRect.top + containerRect.height / 2;
    
    const allMatchingElements = Array.from(container.querySelectorAll(`[data-minute="${currentMinute}"]`)) as HTMLElement[];
    if (allMatchingElements.length === 0) return;
    
    // Find the element closest to the center
    let closestElement: HTMLElement | null = null;
    let closestDistance = Infinity;
    
    allMatchingElements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      const elementCenterY = rect.top + rect.height / 2;
      const distance = Math.abs(centerY - elementCenterY);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestElement = element;
      }
    });
    
    if (closestElement) {
      // Use a small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        if (!userScrollingRef.current.minute && !userClickingRef.current.minute) {
          closestElement!.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [currentMinute]);

  // Initialize scroll position to middle when time picker is first shown
  useEffect(() => {
    if (mode !== 'time' || !showPicker) {
      isInitializingRef.current = false;
      return;
    }
    
    // Set flag to prevent scroll-to-selected effects from interfering
    isInitializingRef.current = true;
    
    // Use requestAnimationFrame to ensure DOM is ready and position instantly
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (hourRef.current && selectedTime) {
          const container = hourRef.current;
          // Scroll to middle of duplicated list, showing the currently selected hour
          const HOUR_REPETITIONS = 40;
          const currentHourValue = selectedTime.hour;
          const targetItem = container.querySelectorAll(`[data-hour="${currentHourValue}"]`)[Math.floor(HOUR_REPETITIONS / 2)] as HTMLElement;
          if (targetItem) {
            // Temporarily disable smooth scrolling for instant positioning
            const originalScrollBehavior = container.style.scrollBehavior;
            container.style.scrollBehavior = 'auto';
            
            // Use scrollIntoView with block: 'center' for accurate centering
            targetItem.scrollIntoView({ behavior: 'auto', block: 'center' });
            
            // Restore original scroll behavior after positioning is complete
            requestAnimationFrame(() => {
              if (hourRef.current) {
                hourRef.current.style.scrollBehavior = originalScrollBehavior || '';
              }
            });
          }
        }
        
        if (minuteRef.current && selectedTime) {
          const container = minuteRef.current;
          // Scroll to middle of duplicated list, showing the currently selected minute
          const MINUTE_REPETITIONS = 40 * 4;
          const currentMinuteValue = selectedTime.minute;
          const targetItem = container.querySelectorAll(`[data-minute="${currentMinuteValue}"]`)[Math.floor(MINUTE_REPETITIONS / 2)] as HTMLElement;
          if (targetItem) {
            // Temporarily disable smooth scrolling for instant positioning
            const originalScrollBehavior = container.style.scrollBehavior;
            container.style.scrollBehavior = 'auto';
            
            // Use scrollIntoView with block: 'center' for accurate centering
            targetItem.scrollIntoView({ behavior: 'auto', block: 'center' });
            
            // Restore original scroll behavior after positioning is complete
            requestAnimationFrame(() => {
              if (minuteRef.current) {
                minuteRef.current.style.scrollBehavior = originalScrollBehavior || '';
              }
            });
          }
        }
        
        // Clear the initialization flag after a brief delay to allow scroll-to-selected to work
        setTimeout(() => {
          isInitializingRef.current = false;
        }, 200);
      });
    });
  }, [mode, showPicker]);

  const formatDisplayValue = () => {
    if (!selected) return 'Select date and time';
    const date = selected.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const time = selected.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${date} at ${time}`;
  };

  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date);
  };

  const handleDateConfirm = () => {
    if (selectedDate) {
      setMode('time');
      const minTime = getMinTimeForSelectedDate();
      const now = new Date();
      let initialTime = selectedTime
        ? { hour: selectedTime.hour, minute: selectedTime.minute }
        : { hour: now.getHours(), minute: roundMinuteUpToTen(now.getMinutes()) };
      if (initialTime.minute === 60) {
        initialTime = { hour: initialTime.hour + 1, minute: 0 };
      }
      if (initialTime.hour >= 24) initialTime = { hour: 23, minute: 50 };

      // When selected date is today, ensure initial time is not in the past
      if (minTime) {
        if (initialTime.hour < minTime.hour || (initialTime.hour === minTime.hour && initialTime.minute < minTime.minute)) {
          initialTime = { hour: minTime.hour, minute: minTime.minute };
        }
      } else if (!selectedTime) {
        initialTime = { hour: 9, minute: 0 };
      }

      setSelectedTime(initialTime);
      const dateWithTime = new Date(selectedDate);
      dateWithTime.setHours(initialTime.hour, initialTime.minute, 0, 0);
      onChange(dateWithTime);
    }
  };

  // Scroll the hour/minute wheels to show the given time (used after clamping so UI stays in sync)
  const scrollTimePickerTo = (hour: number, minute: number) => {
    const HOUR_REPETITIONS = 40;
    const MINUTE_REPETITIONS = 40 * 4;
    requestAnimationFrame(() => {
      if (hourRef.current) {
        const hourItems = hourRef.current.querySelectorAll(`[data-hour="${hour}"]`);
        const midIndex = Math.floor(hourItems.length / 2);
        const targetHour = hourItems[midIndex] as HTMLElement | undefined;
        if (targetHour) {
          const origScrollBehavior = hourRef.current.style.scrollBehavior;
          hourRef.current.style.scrollBehavior = 'smooth';
          targetHour.scrollIntoView({ block: 'center', behavior: 'smooth' });
          hourRef.current.style.scrollBehavior = origScrollBehavior || '';
        }
      }
      if (minuteRef.current) {
        const minuteItems = minuteRef.current.querySelectorAll(`[data-minute="${minute}"]`);
        const midIndex = Math.floor(minuteItems.length / 2);
        const targetMinute = minuteItems[midIndex] as HTMLElement | undefined;
        if (targetMinute) {
          const origScrollBehavior = minuteRef.current.style.scrollBehavior;
          minuteRef.current.style.scrollBehavior = 'smooth';
          targetMinute.scrollIntoView({ block: 'center', behavior: 'smooth' });
          minuteRef.current.style.scrollBehavior = origScrollBehavior || '';
        }
      }
    });
  };

  const handleTimeSelect = (hour: number, minute: number) => {
    const roundedMinuteInput = roundMinuteToTen(minute);
    let roundedMinute = roundedMinuteInput;
    let finalHour = hour;

    // When selected date is today, clamp to minimum time (no past times)
    const minTime = getMinTimeForSelectedDate();
    const didClamp = minTime && (hour < minTime.hour || (hour === minTime.hour && roundedMinuteInput < minTime.minute));
    if (minTime) {
      if (hour < minTime.hour) {
        finalHour = minTime.hour;
        roundedMinute = minTime.minute;
      } else if (hour === minTime.hour && roundedMinute < minTime.minute) {
        roundedMinute = minTime.minute;
      }
    }

    setSelectedTime({ hour: finalHour, minute: roundedMinute });
    if (selectedDate) {
      const newDate = new Date(selectedDate);
      newDate.setHours(finalHour, roundedMinute, 0, 0);
      onChange(newDate);
    }
    // When we clamped, scroll the wheels back to the clamped time so they don't stay on the past value
    if (didClamp) {
      setTimeout(() => scrollTimePickerTo(finalHour, roundedMinute), 50);
    }
  };

  const handleBackToDate = () => {
    setMode('date');
  };

  const handleConfirm = () => {
    if (selectedDate && selectedTime) {
      const finalDate = new Date(selectedDate);
      finalDate.setHours(selectedTime.hour, selectedTime.minute, 0, 0);
      onChange(finalDate);
      setShowPicker(false);
      setMode('date');
    }
  };

  const handleCancel = () => {
    setShowPicker(false);
    setMode('date');
    // Reset to original selected value
    if (selected) {
      setSelectedDate(selected);
      setSelectedTime({ hour: selected.getHours(), minute: roundMinuteToTen(selected.getMinutes()) });
    } else {
      setSelectedDate(null);
      setSelectedTime(null);
    }
  };

  // Handle scroll to detect which item is in the center
  const handleHourScroll = () => {
    if (!hourRef.current || !selectedTime) return;
    
    const container = hourRef.current;
    
    // Mark that user is actively scrolling
    userScrollingRef.current.hour = true;
    
    // Clear any existing timeout
    if (scrollTimeoutRef.current.hour) {
      clearTimeout(scrollTimeoutRef.current.hour);
    }
    
    // Reset user scrolling flag after scroll stops
    scrollTimeoutRef.current.hour = setTimeout(() => {
      userScrollingRef.current.hour = false;
    }, 150);
    
    // Debounce center item detection using requestAnimationFrame
    if (isScrollingRef.current.hour) return;
    isScrollingRef.current.hour = true;
    
    requestAnimationFrame(() => {
      if (!hourRef.current) {
        isScrollingRef.current.hour = false;
        return;
      }
      
      const containerRect = container.getBoundingClientRect();
      const centerY = containerRect.top + containerRect.height / 2;
      
      const items = Array.from(container.querySelectorAll('[data-hour]')) as HTMLElement[];
      let closestItem: HTMLElement | null = null;
      let closestDistance = Infinity;
      
      items.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const itemCenterY = rect.top + rect.height / 2;
        const distance = Math.abs(centerY - itemCenterY);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestItem = element;
        }
      });
      
      if (closestItem !== null) {
        const hourAttr = (closestItem as HTMLElement).getAttribute('data-hour');
        if (hourAttr !== null) {
          const hour = parseInt(hourAttr, 10);
          if (hour !== selectedTime.hour) {
            handleTimeSelect(hour, selectedTime.minute);
          }
        }
      }
      
      isScrollingRef.current.hour = false;
    });
  };

  const handleMinuteScroll = () => {
    if (!minuteRef.current || !selectedTime) return;
    
    const container = minuteRef.current;
    
    // Mark that user is actively scrolling
    userScrollingRef.current.minute = true;
    
    // Clear any existing timeout
    if (scrollTimeoutRef.current.minute) {
      clearTimeout(scrollTimeoutRef.current.minute);
    }
    
    // Reset user scrolling flag after scroll stops
    scrollTimeoutRef.current.minute = setTimeout(() => {
      userScrollingRef.current.minute = false;
    }, 150);
    
    // Debounce center item detection using requestAnimationFrame
    if (isScrollingRef.current.minute) return;
    isScrollingRef.current.minute = true;
    
    requestAnimationFrame(() => {
      if (!minuteRef.current) {
        isScrollingRef.current.minute = false;
        return;
      }
      
      const containerRect = container.getBoundingClientRect();
      const centerY = containerRect.top + containerRect.height / 2;
      
      const items = Array.from(container.querySelectorAll('[data-minute]')) as HTMLElement[];
      let closestItem: HTMLElement | null = null;
      let closestDistance = Infinity;
      
      items.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const itemCenterY = rect.top + rect.height / 2;
        const distance = Math.abs(centerY - itemCenterY);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestItem = element;
        }
      });
      
      if (closestItem !== null) {
        const minuteAttr = (closestItem as HTMLElement).getAttribute('data-minute');
        if (minuteAttr !== null) {
          const minute = parseInt(minuteAttr, 10);
          if (minute !== selectedTime.minute) {
            handleTimeSelect(selectedTime.hour, minute);
          }
        }
      }
      
      isScrollingRef.current.minute = false;
    });
  };

  // Vertical slider time picker
  const renderTimePicker = () => {
    const currentHour = selectedTime ? selectedTime.hour : 0;
    const currentMinute = selectedTime ? selectedTime.minute : 0;

    // Create duplicated arrays for infinite scroll effect
    const HOUR_REPETITIONS = 40; // Number of times to repeat the hour sequence
    const MINUTE_REPETITIONS = 40 * 4; // Number of times to repeat the minute sequence
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const minutes = Array.from({ length: 6 }, (_, i) => i * 10); // Only multiples of 10: 0, 10, 20, 30, 40, 50
    
    // Duplicate hours and minutes
    const duplicatedHours = Array(HOUR_REPETITIONS).fill(hours).flat();
    const duplicatedMinutes = Array(MINUTE_REPETITIONS).fill(minutes).flat();

    return (
      <div className={styles.timePickerContainer}>
        <div className={styles.verticalSliders}>
          {/* Hour Slider */}
          <div className={styles.sliderContainer}>
            <label className={styles.sliderLabel}>Hour</label>
            <div 
              className={styles.verticalSlider} 
              ref={hourRef}
              onScroll={handleHourScroll}
            >
              {duplicatedHours.map((hour, index) => (
                <button
                  key={`hour-${index}`}
                  type="button"
                  className={`${styles.sliderItem} ${currentHour === hour ? styles.sliderItemActive : ''}`}
                  data-hour={hour}
                  data-hour-index={index}
                  onClick={(e) => {
                    // Prevent scroll detection from interfering
                    userClickingRef.current.hour = true;
                    
                    // Get the clicked element
                    const clickedElement = e.currentTarget;
                    const clickedHour = hour;
                    
                    // Immediately select the clicked item
                    handleTimeSelect(clickedHour, currentMinute);
                    
                    // Optionally, smoothly scroll the clicked item to center if it's not already centered
                    // This ensures the clicked item stays visible
                    requestAnimationFrame(() => {
                      const container = hourRef.current;
                      if (container && clickedElement) {
                        const containerRect = container.getBoundingClientRect();
                        const elementRect = clickedElement.getBoundingClientRect();
                        const containerCenter = containerRect.top + containerRect.height / 2;
                        const elementCenter = elementRect.top + elementRect.height / 2;
                        const offset = elementCenter - containerCenter;
                        
                        // Only scroll if the element is significantly off-center
                        if (Math.abs(offset) > 20) {
                          container.scrollBy({
                            top: offset,
                            behavior: 'smooth'
                          });
                        }
                      }
                    });
                    
                    // Reset the flag after a delay to allow scroll detection to resume
                    setTimeout(() => {
                      userClickingRef.current.hour = false;
                    }, 300);
                  }}
                >
                  {hour.toString().padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>

          {/* Minute Slider */}
          <div className={styles.sliderContainer}>
            <label className={styles.sliderLabel}>Minute</label>
            <div 
              className={styles.verticalSlider} 
              ref={minuteRef}
              onScroll={handleMinuteScroll}
            >
              {duplicatedMinutes.map((minute, index) => (
                <button
                  key={`minute-${index}`}
                  type="button"
                  className={`${styles.sliderItem} ${currentMinute === minute ? styles.sliderItemActive : ''}`}
                  data-minute={minute}
                  data-minute-index={index}
                  onClick={(e) => {
                    // Prevent scroll detection from interfering
                    userClickingRef.current.minute = true;
                    
                    // Get the clicked element
                    const clickedElement = e.currentTarget;
                    const clickedMinute = minute;
                    
                    // Immediately select the clicked item
                    handleTimeSelect(currentHour, clickedMinute);
                    
                    // Optionally, smoothly scroll the clicked item to center if it's not already centered
                    // This ensures the clicked item stays visible
                    requestAnimationFrame(() => {
                      const container = minuteRef.current;
                      if (container && clickedElement) {
                        const containerRect = container.getBoundingClientRect();
                        const elementRect = clickedElement.getBoundingClientRect();
                        const containerCenter = containerRect.top + containerRect.height / 2;
                        const elementCenter = elementRect.top + elementRect.height / 2;
                        const offset = elementCenter - containerCenter;
                        
                        // Only scroll if the element is significantly off-center
                        if (Math.abs(offset) > 20) {
                          container.scrollBy({
                            top: offset,
                            behavior: 'smooth'
                          });
                        }
                      }
                    });
                    
                    // Reset the flag after a delay to allow scroll detection to resume
                    setTimeout(() => {
                      userClickingRef.current.minute = false;
                    }, 300);
                  }}
                >
                  {minute.toString().padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Cleanup scroll timeouts on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current.hour) {
        clearTimeout(scrollTimeoutRef.current.hour);
      }
      if (scrollTimeoutRef.current.minute) {
        clearTimeout(scrollTimeoutRef.current.minute);
      }
    };
  }, []);

  useEffect(() => {
    if (showPicker && containerRef.current) {
      document.body.style.overflow = 'hidden';

      const updatePosition = () => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = mode === 'date' ? 450 : 500;
        const isMobile = viewportWidth <= 768;

        if (isMobile) {
          setUseFixedPosition(true);
          setDropdownPosition('bottom');
        } else {
          setUseFixedPosition(false);
          if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            setDropdownPosition('top');
          } else {
            setDropdownPosition('bottom');
          }
        }
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    } else {
      setUseFixedPosition(false);
    }
  }, [showPicker, mode]);

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setShowPicker(!showPicker)}
      >
        <span className={styles.triggerText}>{formatDisplayValue()}</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={styles.icon}
        >
          <path
            d="M6 2V4M14 2V4M3 8H17M5 4H15C16.1046 4 17 4.89543 17 6V16C17 17.1046 16.1046 18 15 18H5C3.89543 18 3 17.1046 3 16V6C3 4.89543 3.89543 4 5 4Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {showPicker && (
        <div
          ref={dropdownRef}
          className={`${styles.dropdown} ${dropdownPosition === 'top' ? styles.dropdownTop : ''} ${useFixedPosition ? styles.dropdownFixed : ''}`}
        >
          <div className={styles.dropdownHeader}>
            <h3 className={styles.dropdownTitle}>
              {mode === 'date' ? (
                <>
                  Select date: {selectedDate ? selectedDate.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  }) : '--'}
                </>
              ) : (
                <>
                  Select time: {selectedTime ? `${selectedTime.hour.toString().padStart(2, '0')}:${selectedTime.minute.toString().padStart(2, '0')}` : '--:--'}
                </>
              )}
            </h3>
            {mode === 'time' && (
              <button
                type="button"
                className={styles.changeDateButton}
                onClick={handleBackToDate}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Change Date
              </button>
            )}
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleCancel}
            >
              ×
            </button>
          </div>

          <div className={styles.pickerContent}>
            {mode === 'date' ? (
              <div className={styles.calendarSection}>
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateSelect}
                  inline
                  minDate={minDate || new Date()}
                  maxDate={maxDate}
                  calendarClassName={styles.calendar}
                  openToDate={selectedDate || new Date()}
                />
              </div>
            ) : (
              <div className={styles.timeSection}>
                {renderTimePicker()}
              </div>
            )}
          </div>

          <div className={styles.dropdownFooter}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancel}
            >
              Cancel
            </button>
            {mode === 'date' ? (
              <button
                type="button"
                className={styles.confirmButton}
                onClick={handleDateConfirm}
                disabled={!selectedDate}
              >
                Continue to Time
              </button>
            ) : (
              <button
                type="button"
                className={styles.confirmButton}
                onClick={handleConfirm}
                disabled={!selectedDate || !selectedTime}
              >
                Confirm
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
